import * as yup from "yup";
import React, { useEffect, useState } from "react";

import { Grid, MenuItem, TextField, Typography } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";

import useAxios from "../../../../hooks/useAxios";
import useAlert from "../../../../hooks/useAlert";
import {
  __createTmobilejCallFilter,
  __deleteTmobilejCallFilter,
  __getTmobilejCuratedGroups,
  __updateTmobilejCallFilter,
} from "../../../../api/sim";
import {
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  OutlinedInput,
  Select,
} from "@mui/material";

const lineValidationSchema = yup.object({
  number: yup
    .string("number")
    .min(3, "Should be between 3 and 10 digits long")
    .max(10, "Should be between 3 and 10 digits long")
    .required(),
});

const validateNumbersText = (data) => {
  const errors = {};
  if (!data) return { errors, numbers: [] };

  const numbers = [];
  const lines = data.split("\n");
  lines.some((line, idx) => {
    const trimmed = String(line || "").trim();
    if (!trimmed) return false; // allow empty lines

    const fields = trimmed.split(",");
    if (fields.length < 1) {
      errors.lines = `Invalid format in line ${idx + 1}: ${line}`;
      return true;
    }

    try {
      const obtainedData = { number: fields[0].trim() };
      lineValidationSchema.validateSync(obtainedData);
      if (isNaN(obtainedData.number)) throw new Error("Invalid Number");

      numbers.push(obtainedData.number);
      if (numbers.indexOf(obtainedData.number) !== numbers.length - 1) {
        errors.lines = `Duplicate number value in line ${idx + 1}`;
        return true;
      }
    } catch (err) {
      errors.lines = `Invalid format in line ${idx + 1}: ${line}`;
      return true;
    }

    return false;
  });

  return { errors, numbers };
};

const fromE164To10Digits = (value) => {
  const s = String(value || "").trim();
  if (/^\+1\d+$/.test(s)) return s.slice(2);
  if (/^\d{11}$/.test(s) && s.startsWith("1")) return s.slice(1);
  if (/^\d{10}$/.test(s)) return s;
  return "";
};

const stringify10DigitList = (nums) => {
  if (!Array.isArray(nums) || nums.length === 0) return "";
  return nums
    .map((n) => {
      const str = String(n || "").trim();
      // Accept E.164 with +1 or 11 digit 1xxxxxxxxxx or 10 digits, or 3-9 digits (short code)
      if (/^\+1\d{10}$/.test(str)) return str.slice(2);
      if (/^1\d{10}$/.test(str)) return str.slice(1);
      if (/^\d{10}$/.test(str)) return str;
      if (/^\d{3,9}$/.test(str)) return str; // Allow short code (3-9 digits)
      return "";
    })
    .filter(Boolean)
    .join("\n");
};

const pickFilterId = (filter) =>
  filter?.FilterId ||
  filter?.CallFilterId ||
  filter?.filterId ||
  filter?.id ||
  null;

const pickFilterMode = (filter) =>
  filter?.FilterMode || filter?.filterMode || filter?.mode || "WHITELIST";

const pickAllowed = (filter) =>
  filter?.PersonalNumbers ||
  filter?.AllowedNumbers ||
  filter?.allowedNumbers ||
  filter?.Allowed ||
  [];

const pickBlocked = (filter) =>
  filter?.PersonalNumbers ||
  filter?.BlockedNumbers ||
  filter?.blockedNumbers ||
  filter?.Blocked ||
  [];

const CallFilters = ({
  phone,
  subscriberId: propSubscriberId,
  phoneError,
  validatePhone,
  initialData,
  onDataUpdate,
}) => {
  const { setAlertMessage } = useAlert();

  const [filterId, setFilterId] = useState(null);
  const [mode, setMode] = useState("WHITELIST");
  const [allowedText, setAllowedText] = useState("");
  const [blockedText, setBlockedText] = useState("");
  const [allowedNumbers, setAllowedNumbers] = useState([]);
  const [blockedNumbers, setBlockedNumbers] = useState([]);
  const [allowedError, setAllowedError] = useState("");
  const [blockedError, setBlockedError] = useState("");
  // View-only fields from API response
  const [applyToOutbound, setApplyToOutbound] = useState(null);
  const [applyToInbound, setApplyToInbound] = useState(null);
  const [blockUnknownNumbers, setBlockUnknownNumbers] = useState(null);
  const [blockInternational, setBlockInternational] = useState(null);
  const [recordFlaggedCalls, setRecordFlaggedCalls] = useState(null);

  const [availableGroups, setAvailableGroups] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);

  const [groupsResp, fetchGroups] = useAxios();

  useEffect(() => {
    fetchGroups(__getTmobilejCuratedGroups());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (groupsResp?.data) {
      setAvailableGroups(groupsResp.data);
    }
  }, [groupsResp]);

  const validateLists = () => {
    if (mode === "WHITELIST") {
      const { errors, numbers } = validateNumbersText(allowedText);
      setAllowedError(errors.lines || "");
      setAllowedNumbers(errors.lines ? [] : numbers);
      return !errors.lines;
    }
    const { errors, numbers } = validateNumbersText(blockedText);
    setBlockedError(errors.lines || "");
    setBlockedNumbers(errors.lines ? [] : numbers);
    return !errors.lines;
  };

  const [createResp, createCall, createLoading] = useAxios();
  const [updateResp, updateCall, updateLoading] = useAxios();
  const [deleteResp, deleteCall, deleteLoading] = useAxios();

  const hydrateFromData = (data) => {
    // Backend returns the filter object directly or wrapped.
    // Based on tmobilej.py, it returns service.get_call_filter_by_subscriber_id(subscriber_id)
    // which likely returns the filter object or a list.
    const f = data?.filter || data || null;
    setFilterId(pickFilterId(f));
    setMode(pickFilterMode(f));
    const allowedStr = stringify10DigitList(pickAllowed(f));
    const blockedStr = stringify10DigitList(pickBlocked(f));

    setAllowedText(allowedStr);
    setBlockedText(blockedStr);

    const allowedParsed = validateNumbersText(allowedStr);
    setAllowedError(allowedParsed.errors.lines || "");
    setAllowedNumbers(allowedParsed.errors.lines ? [] : allowedParsed.numbers);

    const blockedParsed = validateNumbersText(blockedStr);
    setBlockedError(blockedParsed.errors.lines || "");
    setBlockedNumbers(blockedParsed.errors.lines ? [] : blockedParsed.numbers);

    // Extract view-only fields
    setApplyToOutbound(f?.ApplyToOutbound ?? null);
    setApplyToInbound(f?.ApplyToInbound ?? null);
    setBlockUnknownNumbers(f?.BlockUnknownNumbers ?? null);
    setBlockInternational(f?.BlockInternational ?? null);
    setRecordFlaggedCalls(f?.RecordFlaggedCalls ?? null);

    const selGroups = f?.SelectedGroups || [];
    setSelectedGroupIds(selGroups.map((g) => g.id));
  };

  // Hydrate form when initialData changes
  useEffect(() => {
    hydrateFromData(initialData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  useEffect(() => {
    if (!createResp?.status) return;
    setAlertMessage({ type: "success", message: "Filter created" });
    if (createResp.data && onDataUpdate) {
      onDataUpdate(createResp.data);
    }
    hydrateFromData(createResp.data);
  }, [createResp]);

  useEffect(() => {
    if (!updateResp?.status) return;
    setAlertMessage({ type: "success", message: "Filter updated" });
    if (updateResp.data && onDataUpdate) {
      onDataUpdate(updateResp.data);
    }
    hydrateFromData(updateResp.data);
  }, [updateResp]);

  useEffect(() => {
    if (!deleteResp?.status) return;
    setAlertMessage({ type: "success", message: "Filter deleted" });
    setFilterId(null);
    setAllowedText("");
    setBlockedText("");
    setAllowedNumbers([]);
    setBlockedNumbers([]);
    setAllowedError("");
    setBlockedError("");
    if (onDataUpdate) {
      onDataUpdate(null);
    }
  }, [deleteResp]);

  const buildPayload = () => {
    const payload = {
      Phone: phone,
      FilterMode: mode,
      ApplyToOutbound: true,
      ApplyToInbound: true,
      BlockUnknownNumbers: true,
      BlockInternational: true,
      RecordFlaggedCalls: true,
      EnableTranscription: true,
      SelectedGroupIds: selectedGroupIds,
    };
    if (mode === "WHITELIST") {
      payload.AllowedNumbers = allowedNumbers;
    } else {
      payload.BlockedNumbers = blockedNumbers;
    }
    return payload;
  };

  const handleCreate = async () => {
    try {
      const err = validatePhone(phone);
      if (err) throw new Error(err);
      if (!validateLists()) throw new Error("Fix invalid numbers");
      const payload = buildPayload();
      await createCall({
        ...__createTmobilejCallFilter(propSubscriberId),
        data: payload,
      });
    } catch (e) {
      setAlertMessage({ type: "error", message: e.message || "Invalid input" });
    }
  };

  const handleUpdate = async () => {
    if (!filterId) return;
    try {
      const err = validatePhone(phone);
      if (err) throw new Error(err);
      if (!validateLists()) throw new Error("Fix invalid numbers");
      const payload = buildPayload();
      await updateCall({
        ...__updateTmobilejCallFilter(propSubscriberId, filterId),
        data: payload,
      });
    } catch (e) {
      setAlertMessage({ type: "error", message: e.message || "Invalid input" });
    }
  };

  const handleDelete = async () => {
    if (!filterId) return;
    await deleteCall(__deleteTmobilejCallFilter(propSubscriberId, filterId));
  };

  const loading = createLoading || updateLoading || deleteLoading;
  const hasFilter = Boolean(filterId);
  const hasErrors =
    Boolean(phoneError) ||
    (mode === "WHITELIST" ? Boolean(allowedError) : Boolean(blockedError));

  return (
    <>
      <Grid item sx={{ mt: 2 }}>
        <Typography variant="body2">
          SubscriberId: {propSubscriberId || "—"} | FilterId: {filterId || "—"}
        </Typography>
      </Grid>

      {!hasFilter && (
        <Grid item>
          <Typography variant="subtitle2">No filters available.</Typography>
        </Grid>
      )}

      {hasFilter && (
        <Grid item>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Filter Settings (View Only):
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            ApplyToOutbound: {String(applyToOutbound ?? "—")} | ApplyToInbound:{" "}
            {String(applyToInbound ?? "—")}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            BlockUnknownNumbers: {String(blockUnknownNumbers ?? "—")} |
            BlockInternational: {String(blockInternational ?? "—")}
          </Typography>
          <Typography variant="body2">
            RecordFlaggedCalls: {String(recordFlaggedCalls ?? "—")}
          </Typography>
        </Grid>
      )}

      <>
        <Grid item sx={{ mt: 1 }}>
          <TextField
            select
            fullWidth
            label="Filter Mode"
            value={mode}
            onChange={(e) => {
              const next = e.target.value;
              setMode(next);
              if (next === "WHITELIST") {
                const parsed = validateNumbersText(allowedText);
                setAllowedError(parsed.errors.lines || "");
                setAllowedNumbers(parsed.errors.lines ? [] : parsed.numbers);
              } else {
                const parsed = validateNumbersText(blockedText);
                setBlockedError(parsed.errors.lines || "");
                setBlockedNumbers(parsed.errors.lines ? [] : parsed.numbers);
              }
            }}
          >
            <MenuItem value="WHITELIST">WHITELIST</MenuItem>
            <MenuItem value="BLACKLIST">BLACKLIST</MenuItem>
          </TextField>
        </Grid>

        <Grid item sx={{ mt: 1 }}>
          <FormControl fullWidth>
            <InputLabel id="curated-groups-label">Curated Groups</InputLabel>
            <Select
              labelId="curated-groups-label"
              multiple
              value={selectedGroupIds}
              onChange={(e) => setSelectedGroupIds(e.target.value)}
              input={<OutlinedInput label="Curated Groups" />}
              renderValue={(selected) => {
                return availableGroups
                  .filter((g) => selected.includes(g.id))
                  .map((g) => g.name)
                  .join(", ");
              }}
            >
              {availableGroups.map((group) => (
                <MenuItem key={group.id} value={group.id}>
                  <Checkbox checked={selectedGroupIds.indexOf(group.id) > -1} />
                  <ListItemText primary={group.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {mode === "WHITELIST" ? (
          <Grid item>
            <TextField
              fullWidth
              multiline
              minRows={4}
              label="Allowed Numbers (10 digits or short codes, one per line)"
              value={allowedText}
              error={Boolean(allowedError)}
              helperText={
                allowedError ||
                "Digits only; 10-digit numbers or short codes (3-9 digits); one per line"
              }
              onChange={(e) => {
                const text = e.target.value;
                setAllowedText(text);
                const parsed = validateNumbersText(text);
                setAllowedError(parsed.errors.lines || "");
                setAllowedNumbers(parsed.errors.lines ? [] : parsed.numbers);
              }}
            />
          </Grid>
        ) : (
          <Grid item>
            <TextField
              fullWidth
              multiline
              minRows={4}
              label="Blocked Numbers (10 digits or short codes, one per line)"
              value={blockedText}
              error={Boolean(blockedError)}
              helperText={
                blockedError ||
                "Digits only; 10-digit numbers or short codes (3-9 digits); one per line"
              }
              onChange={(e) => {
                const text = e.target.value;
                setBlockedText(text);
                const parsed = validateNumbersText(text);
                setBlockedError(parsed.errors.lines || "");
                setBlockedNumbers(parsed.errors.lines ? [] : parsed.numbers);
              }}
            />
          </Grid>
        )}

        <Grid item sx={{ display: "flex", gap: 1 }}>
          {!hasFilter ? (
            <LoadingButton
              loading={createLoading}
              disabled={loading || hasErrors}
              variant="contained"
              onClick={handleCreate}
            >
              Create Call Filter
            </LoadingButton>
          ) : (
            <>
              <LoadingButton
                loading={updateLoading}
                disabled={loading || hasErrors}
                variant="contained"
                onClick={handleUpdate}
              >
                Update Call Filter
              </LoadingButton>
              <LoadingButton
                loading={deleteLoading}
                disabled={loading}
                color="error"
                variant="outlined"
                onClick={handleDelete}
              >
                Delete Call Filter
              </LoadingButton>
            </>
          )}
        </Grid>
      </>
    </>
  );
};

export default CallFilters;
